'use client';

import HeaderCardsList from '@/components/Header/HeaderCards/HeaderCardsList';
import PageNavBlock from '@/components/PageNav/pageNavBlock';
import { localeText, localizedMenu } from '@/utils/constantTxt/localeText';
import { useCurrentLocale } from '@/utils/hooks/useCurrentLocale';
import { Dialog, Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ArrowUpRightIcon, Bars3Icon, PhoneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import LocalizedLink from '../LocalizedLink';
import LocaleSwitcher from '../LocaleSwitcher';
import AnimationBanner from '@/components/Banner/AnimationBanner';

interface MenuTitle {
  id: number;
  name: string;
  href: string;
  priceName?: string;
  hrefPrice?: string;
  color?: 'violet' | 'blue' | 'green';
  submenu?: Array<{
    name: string;
    href: string;
    subtitle: Array<{
      name: string;
      href: string;
    }>;
  }>;
}

export interface HeaderCustomProps {
  color?: 'violet' | 'green' | 'blue';
  srcLogo?: string;
  logoLink?: string;
  pageNav?: boolean;
  navList?: Array<{
    name: string;
    href: string;
    current: boolean;
  }>;
  menuTitle?: MenuTitle[];
}

export default function HeaderCustom({
  color = 'green',
  srcLogo = '/assets/icon/LogoRevers.svg',
  logoLink = '/',
  pageNav = false,
  navList,
}: HeaderCustomProps) {
  const loc = useCurrentLocale();
  const menuTitle = [
    {
      id: 1,
      name: localizedMenu[loc].home,
      href: '/',
    },
    {
      id: 2,
      name: 'AP School',
      href: '/school',
      priceName: localizedMenu[loc].price,
      hrefPrice: '/school/#price',
      color: 'violet' as const,
      submenu: [
        { name: localizedMenu[loc].preschool, href: '/school/doshkilna-osvita', subtitle: [] },
        {
          name: localizedMenu[loc].primary_school,
          href: '/school/pochatkova-shkola',
          subtitle: [],
        },
        {
          name: localizedMenu[loc].middle_high_school,
          href: '/school/serednia-starsha-shkola',
          subtitle: [],
        },
        { name: localizedMenu[loc].camps, href: '/school/camp', subtitle: [] },
        { name: localizedMenu[loc].nmt_prep, href: '/school/pidgotovka-do-nmt', subtitle: [] },
      ],
    },
    {
      id: 3,
      name: 'AP University',
      href: '/university',
      priceName: localizedMenu[loc].price,
      hrefPrice: '/university/#price',
      color: 'blue' as const,
      submenu: [
        { name: localizedMenu[loc].admission, href: '/university/vstup', subtitle: [] },
        { name: localizedMenu[loc].abroad_edu, href: '/university/abroad-education', subtitle: [] },
        { name: localizedMenu[loc].internship, href: '/university/stazhuvannia', subtitle: [] },
        {
          name: localizedMenu[loc].exchange_program,
          href: '/university/programy-obminu',
          subtitle: [],
        },
        {
          name: localizedMenu[loc].grants,
          href: '/university/grants-and-schoolarships',
          subtitle: [],
        },
      ],
    },
    {
      id: 4,
      name: localizedMenu[loc].language,
      href: '/movni-kursy',
      priceName: localizedMenu[loc].price,
      hrefPrice: '/price',
      color: 'green' as const,
      submenu: [
        {
          name: localizedMenu[loc].english,
          href: '/movni-kursy/english',
          subtitle: [
            { name: localizedMenu[loc].english_kids, href: '/movni-kursy/english/kids' },
            {
              name: localizedMenu[loc].english_group,
              href: '/movni-kursy/english/grupove-navchannia',
            },
            {
              name: localizedMenu[loc].english_individual,
              href: '/movni-kursy/english/indyvidualne-navchannia',
            },
            { name: localizedMenu[loc].cambridge, href: '/movni-kursy/english/languagecert-esol' },
            { name: localizedMenu[loc].english_tests, href: '/movni-kursy/english/tests' },
            { name: localizedMenu[loc].toefl, href: '/movni-kursy/english/toefl' },
            { name: localizedMenu[loc].ielts, href: '/movni-kursy/english/ielts' },
            { name: localizedMenu[loc].toeic, href: '/movni-kursy/english/toeic' },
          ],
        },
        {
          name: localizedMenu[loc].german,
          href: '/movni-kursy/deutsch',
          subtitle: [
            { name: localizedMenu[loc].german_kids, href: '/movni-kursy/deutsch/kids' },
            {
              name: localizedMenu[loc].german_group,
              href: '/movni-kursy/deutsch/grupove-navchannia',
            },
            {
              name: localizedMenu[loc].german_individual,
              href: '/movni-kursy/deutsch/indyvidualne-navchannia',
            },
            { name: localizedMenu[loc].german_tests, href: '/movni-kursy/deutsch/tests' },
          ],
        },
        {
          name: localizedMenu[loc].polish,
          href: '/movni-kursy/polski',
          subtitle: [
            { name: localizedMenu[loc].polish_kids, href: '/movni-kursy/polski/kids' },
            {
              name: localizedMenu[loc].polish_group,
              href: '/movni-kursy/polski/grupove-navchannia',
            },
            {
              name: localizedMenu[loc].polish_individual,
              href: '/movni-kursy/polski/indyvidualne-navchannia',
            },
            { name: localizedMenu[loc].polish_tests, href: '/movni-kursy/polski/tests' },
            { name: localizedMenu[loc].polish_prep, href: '/pidgotovchi_kursy' },
          ],
        },
        { name: localizedMenu[loc].evi_prep, href: '/movni-kursy/evi', subtitle: [] },
      ],
    },
    {
      id: 5,
      name: localizedMenu[loc].about_us,
      href: '/about-us',
      color: 'green' as const,
      submenu: [
        {
          name: localizedMenu[loc].what_is_ap,
          href: '/about-us/shcho-take-ap-education',
          subtitle: [],
        },
        { name: localizedMenu[loc].career, href: '/about-us/kariera', subtitle: [] },
        { name: localizedMenu[loc].partner, href: '/about-us/stan-partnerom', subtitle: [] },
        { name: localizedMenu[loc].cooperation, href: '/about-us/spivpratsia', subtitle: [] },
        // { name: localizedMenu[loc].blog, href: 'https://blog.ap.education/', subtitle: [] },
      ],
    },
    {
      id: 6,
      name: localizedMenu[loc].contacts,
      href: '/kontakty',
    },
    {
      id: 7,
      name: localizedMenu[loc].prices,
      href: '/price',
    },
  ];

  const backgroundColorClass =
    {
      green: 'custom-gradient-green-btn',
      blue: 'custom-gradient-blue-btn',
      violet: 'custom-gradient-violet-btn',
    }[color] || 'custom-gradient-green-btn';

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const path = usePathname(); // Use useRouter to get the current route

  const calculateBackgroundOpacity = () => {
    const scrollThreshold = 300; // Adjust this value for when the color should be fully opaque
    const opacity = Math.min(scrollY / scrollThreshold, 1);
    return `rgba(0, 0, 0, ${opacity * 0.5})`; // Gradually change to grey rgba(245, 245, 247, 1)
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const activePathName = (link: string): boolean => {
    if (!path) {
      return false;
    }

    const pathName = path.split('/').slice(1);
    const href = link.split('/').slice(1);
    if (href[0]?.length === 0 && pathName[0].length !== 0) {
      return false;
    } else {
      return pathName[0].includes(href[0]);
    }
  };

  return (
    <>
      <header
        data-meta="HeaderCustom"
        className="top-0 isolate"
        style={{ backgroundColor: calculateBackgroundOpacity() }}
      >
        <nav
          aria-label="Global"
          className="relative mx-auto flex items-center lg:gap-4 justify-between p-6 lg:px-2 xl:px-8 z-50"
          ref={menuRef}
        >
        <div className="flex">
          {!mobileMenuOpen && (
            <LocalizedLink href={logoLink} className="-m-1.5 p-1.5">
              <span className="sr-only">App Education</span>
              <Image
                width={100}
                height={38}
                alt=" "
                src={srcLogo}
                className="h-8 w-32 lg:hidden xl:block"
              />
              <Image
                width={100}
                height={38}
                alt=" "
                src={'/assets/icon/AP.svg'}
                className="h-8 w-32 hidden lg:block xl:hidden"
              />
            </LocalizedLink>
          )}
        </div>
        <div className="flex gap-4 lg:hidden">
          {!mobileMenuOpen && (
            <>
              <LocaleSwitcher />
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-white"
              >
                <span className="sr-only">Open main menu</span>
                <Bars3Icon aria-hidden="true" className="h-6 w-6" />
              </button>
            </>
          )}
        </div>
        <div className="hidden lg:flex lg:justify-center flex-shrink-0 lg:gap-x-4 xl:gap-x-6">
          {menuTitle.map(menuItem => (
            <HeaderCardsList
              key={menuItem.id}
              menuItem={menuItem}
              styleCss={activePathName(menuItem.href) ? 'font-bold' : ''} // Apply bold style if active
            />
          ))}
        </div>
        <div className="hidden lg:flex lg:justify-end lg:items-center lg:gap-x-2">
          <LocaleSwitcher />
          <a
            href="tel:+380936707129"
            className="lg:flex lg:gap-x-2 items-center lg:text-xs xl:text-sm font-semibold leading-6 text-white"
          >
            <PhoneIcon className="h-[20px] w-[20px] text-white" />
            +38 (093) 670 71 29
          </a>
          <Link
            href={'https://online.ap.education/Account/Login'}
            className="flex gap-x-4 items-center rounded-full bg-white pl-4 pr-1 py-1 font-semibold border text-gray-900 shadow-lg ring-t-1 hover:bg-gray-50"
          >
            <span className="py-1 lg:text-xs xl:text-base">{localeText[loc].login}</span>
            <div className={`rounded-full ${backgroundColorClass}`}>
              <ArrowUpRightIcon className="text-white lg:h-[16px] lg:w-[16px] xl:h-[20px] xl:w-[20px] m-[4px] xl:m-[6px]" />
            </div>
          </Link>
        </div>
      </nav>
      <Dialog
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        className="lg:hidden"
        as="nav"
      >
        <div className="fixed inset-0 z-10 bg-black bg-opacity-50" aria-hidden="true" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-20 w-full max-w-md overflow-y-auto bg-black text-white px-6 py-6 sm:max-w-sm">
          <div className="flex items-center justify-between">
            <LocalizedLink href={logoLink} className="-m-1.5 p-1.5">
              <span className="sr-only">AP Education</span>
              <Image width={100} height={38} alt=" " src={srcLogo} className="h-8 w-32" />
            </LocalizedLink>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-white"
            >
              <span className="sr-only">Закрити меню</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6">
            <div className="space-y-2">
              {menuTitle.map(item => (
                <Disclosure key={item.name} as="div">
                  {!item.submenu?.length ? (
                    <LocalizedLink
                      className="flex w-full items-center justify-between rounded-lg py-2 px-3 text-base font-semibold hover:bg-gray-700"
                      href={item.href}
                    >
                      {item.name}
                    </LocalizedLink>
                  ) : (
                    <Disclosure.Button className="group flex w-full items-center justify-between rounded-lg py-2 px-3 text-base font-semibold hover:bg-gray-700">
                      {item.submenu?.length && <span>{item.name}</span>}
                      {item.submenu && (
                        <ChevronDownIcon className="h-5 w-5 flex-none" aria-hidden="true" />
                      )}
                    </Disclosure.Button>
                  )}
                  {item.submenu && (
                    <Disclosure.Panel className="space-y-1">
                      <Disclosure.Button
                        key={item.name}
                        as="a"
                        href={item.href}
                        className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold hover:bg-gray-700"
                      >
                        {item.name}
                      </Disclosure.Button>
                      {item.submenu.map(submenuItem => (
                        <Disclosure.Button
                          key={submenuItem.name}
                          as="a"
                          href={submenuItem.href}
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold hover:bg-gray-700"
                        >
                          {submenuItem.name}
                        </Disclosure.Button>
                      ))}
                      <Disclosure.Button
                        key={item.priceName}
                        as="a"
                        href={item.hrefPrice}
                        className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold hover:bg-gray-700"
                      >
                        {item.priceName}
                      </Disclosure.Button>
                    </Disclosure.Panel>
                  )}
                </Disclosure>
              ))}
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
        {pageNav && navList && (
          <div className="hidde xl:block xl:pb-4">
            <PageNavBlock list={navList} />
          </div>
        )}
      </header>
    </>
  );
}
